// src/components/CustomerProfileModal.tsx
import React, { useState, useEffect, useContext, useMemo, useCallback, useRef } from 'react';
// Import Customer type if not already
import { Customer, ApiCountry, ApiState, ApiCity } from '../utils/types';
// Remove fetchCustomer if only used for initial load
import { updateCustomer /*, fetchCustomer */ } from '../utils/api';
import { useLocations } from '../context/LocationContext';
import { EditableField } from './EditableField'; // Ensure this is imported
import { FiChevronDown, FiChevronUp, FiX } from 'react-icons/fi'; // Added FiX
import { toast } from 'react-toastify';

// --- Define props ---
interface CustProfileProps {
    initialCustomerData: Customer;
    onClose?: () => void; // Make onClose optional for cases where it's not needed (like direct rendering)
}

// Helper function to format Date
const formatDateForInput = (date: Date | string | null | undefined): string | undefined => {
    if (!date) return undefined;
    try {
        if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) return date;
        if (date instanceof Date) {
            if (isNaN(date.getTime())) return undefined;
            const year = date.getFullYear();
            const month = (date.getMonth() + 1).toString().padStart(2, '0');
            const day = date.getDate().toString().padStart(2, '0');
            return `${year}-${month}-${day}`;
        }
    } catch (error) { console.error("Error formatting date:", date, error); }
    return undefined;
};


// --- Component Start ---
const CustomerProfileModal: React.FC<CustProfileProps> = ({
    initialCustomerData,
    onClose, // Destructure onClose
}) => {
    const [isCollapsed, setIsCollapsed] = useState<boolean>(false);
    // Initialize state directly from the prop
    const [customer, setCustomer] = useState<Customer | null>(initialCustomerData);
    const [isUpdating, setIsUpdating] = useState<boolean>(false);

    // Location state and hooks
    const {
        countries: countryOptions,
        states: stateOptions,
        cities: cityOptions,
        loadStates,
        loadCities,
        clearStates,
        clearCities,
        loadingCountries,
        loadingStates,
        loadingCities,
    } = useLocations();
    const [selectedCountry, setSelectedCountry] = useState<ApiCountry | undefined>(undefined);
    const [selectedState, setSelectedState] = useState<ApiState | undefined>(undefined);
    const hasInitiatedLoadRef = useRef({ states: false, cities: false });


    const [phoneReminder, setPhoneReminder] = useState<string | null>(null);

    // --- Sync state if initialCustomerData prop changes ---
    useEffect(() => {
        setCustomer(initialCustomerData);
        // Reset location selections based on new initial data
        const initialCountry = countryOptions.find(c => c.name === initialCustomerData?.country);
        setSelectedCountry(initialCountry);
        // Add similar logic for state if needed, considering async loading
        hasInitiatedLoadRef.current = { states: false, cities: false }; // Reset load flags
    }, [initialCustomerData, countryOptions]); // Add countryOptions dependency


    // --- Keep Location useEffects (triggered by customer changes) ---
    useEffect(() => {
        if (customer?.country && countryOptions.length > 0) {
            const country = countryOptions.find(c => c.name === customer.country);
            // Only update selectedCountry if it's different or undefined
            if (country?.id !== selectedCountry?.id) {
                setSelectedCountry(country);
            }
        } else if (!customer?.country && selectedCountry) {
            // Clear selection if customer country is removed
            setSelectedCountry(undefined);
        }
    }, [customer?.country, countryOptions, selectedCountry]); // Added selectedCountry

    useEffect(() => {
        if (selectedCountry?.id && (!hasInitiatedLoadRef.current.states || selectedCountry.name !== customer?.country)) {
            loadStates(selectedCountry.id);
            hasInitiatedLoadRef.current.states = true;
            // If country changed, clear state/city selections
            if (selectedCountry.name !== customer?.country) {
                setSelectedState(undefined);
                clearCities();
                hasInitiatedLoadRef.current.cities = false;
            }
        } else if (!selectedCountry) {
            // If no country selected, clear states/cities
            clearStates();
            clearCities();
            setSelectedState(undefined);
            hasInitiatedLoadRef.current.states = false;
            hasInitiatedLoadRef.current.cities = false;
        }
    }, [selectedCountry, customer?.country, loadStates, clearStates, clearCities]);

    useEffect(() => {
        if (customer?.state && stateOptions.length > 0 && selectedCountry) {
            const state = stateOptions.find(s => s.name === customer.state);
            // Only update selectedState if it's different or undefined
            if (state?.id !== selectedState?.id) {
                setSelectedState(state);
            }
            // Load cities if state is selected and cities haven't been loaded for it
            if (state?.id && (!hasInitiatedLoadRef.current.cities || state.name !== customer?.state)) {
                loadCities(state.id);
                hasInitiatedLoadRef.current.cities = true;
            } else if (!state) {
                // If state becomes invalid/unselected, clear cities
                clearCities();
                hasInitiatedLoadRef.current.cities = false;
            }
        } else if (!customer?.state && selectedCountry) {
            // If customer state is cleared, clear local state/city selections
            setSelectedState(undefined);
            clearCities();
            hasInitiatedLoadRef.current.cities = false;
        }
    }, [customer?.state, stateOptions, selectedCountry, selectedState, loadCities, clearCities]); // Added selectedState

    // Effect 4: Set phone reminder based on country
    useEffect(() => {
        if (selectedCountry?.phonecode) {
            const phoneCode = selectedCountry.phonecode.startsWith('+') ? selectedCountry.phonecode : `+${selectedCountry.phonecode}`;
            const currentPhone = customer?.phone || '';
            if (currentPhone && !currentPhone.startsWith(phoneCode)) {
                setPhoneReminder(`Ensure phone starts with ${phoneCode}`);
            } else {
                setPhoneReminder(null);
            }
        } else {
            setPhoneReminder(null);
        }
    }, [selectedCountry, customer?.phone]);

    const toggleCollapse = () => setIsCollapsed(!isCollapsed);

    // --- Update Handler ---
    const handleApiUpdate = useCallback(async (field: keyof Customer | string, value: string | null) => {
        if (!customer || isUpdating) return;
        const customerField = field as keyof Customer;
        const originalValue = customer[customerField];

        // Optimistic update
        setCustomer(prev => prev ? { ...prev, [customerField]: value } : null);
        setIsUpdating(true);
        const toastId = toast.loading(`Updating ${String(customerField)}...`);

        // Process value (e.g., ensure null for empty strings if needed by backend)
        const processedValue = value === '' ? null : value;

        // Update phone reminder state immediately based on optimistic update
        if (field === 'phone' && selectedCountry?.phonecode) {
            const phoneCode = selectedCountry.phonecode.startsWith('+') ? selectedCountry.phonecode : `+${selectedCountry.phonecode}`;
            const phoneValueStr = processedValue != null ? String(processedValue) : '';
            if (!phoneValueStr || phoneValueStr.startsWith(phoneCode)) {
                setPhoneReminder(null);
            } else {
                setPhoneReminder(`Ensure phone starts with ${phoneCode}`);
            }
        }

        try {
            // Assuming updateCustomer takes customer ID, field, and value
            await updateCustomer(customer.id.toString(), customerField, processedValue ?? ''); // Pass processed value
            toast.update(toastId, {
                render: `${String(customerField)} updated successfully!`,
                type: 'success',
                isLoading: false,
                autoClose: 2000,
            });
            // No need to manually update state here if API returns updated customer,
            // but optimistic update is fine for responsiveness.
        } catch (err: any) {
            const errorMessage = err.response?.data?.message || err.message || `Failed to update ${String(customerField)}.`;
            toast.update(toastId, {
                render: errorMessage,
                type: 'error',
                isLoading: false,
                autoClose: 4000,
            });
            // Revert optimistic update
            setCustomer(prev => prev ? { ...prev, [customerField]: originalValue } : null);

            // Re-evaluate phone reminder on error revert
            if (field === 'phone' && selectedCountry?.phonecode) {
                const phoneCode = selectedCountry.phonecode.startsWith('+') ? selectedCountry.phonecode : `+${selectedCountry.phonecode}`;
                const originalPhoneStr = originalValue != null ? String(originalValue) : '';
                if (!originalPhoneStr || originalPhoneStr.startsWith(phoneCode)) {
                    setPhoneReminder(null);
                } else {
                    setPhoneReminder(`Ensure phone starts with ${phoneCode}`);
                }
            }

        } finally {
            setIsUpdating(false);
        }
    }, [customer, isUpdating, selectedCountry?.phonecode]); // Added selectedCountry.phonecode dependency


    // --- Location Handlers ---
    const handleCountryChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newCountryName = event.target.value;
        const newSelectedCountry = countryOptions.find(c => c.name === newCountryName);
        // Update local state first for immediate UI feedback
        setSelectedCountry(newSelectedCountry);
        setSelectedState(undefined); // Clear state selection
        clearCities(); // Clear city options

        // Trigger API updates for country, state, and city
        handleApiUpdate('country', newSelectedCountry?.name ?? null);
        handleApiUpdate('state', null); // Clear state in backend
        handleApiUpdate('city', null); // Clear city in backend

        // Reset load flags
        hasInitiatedLoadRef.current.states = false;
        hasInitiatedLoadRef.current.cities = false;

        // Update phone reminder based on new country selection
        if (newSelectedCountry?.phonecode && customer) {
            const phoneCode = newSelectedCountry.phonecode.startsWith('+') ? newSelectedCountry.phonecode : `+${newSelectedCountry.phonecode}`;
            if (!customer.phone?.startsWith(phoneCode)) {
                setPhoneReminder(`Ensure phone starts with ${phoneCode}`);
            } else {
                setPhoneReminder(null);
            }
        } else {
            setPhoneReminder(null);
        }
    };


    const handleStateChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newStateName = event.target.value;
        const newSelectedState = stateOptions.find(s => s.name === newStateName);
        // Update local state first
        setSelectedState(newSelectedState);
        clearCities(); // Clear city options

        // Trigger API updates for state and city
        handleApiUpdate('state', newSelectedState?.name ?? null);
        handleApiUpdate('city', null); // Clear city in backend

        // Reset city load flag
        hasInitiatedLoadRef.current.cities = false;
    };

    const handleCityChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        // Directly update the city via API
        handleApiUpdate('city', event.target.value || null);
    };

    // --- Render Logic ---
    if (!customer) {
        // This might happen briefly if initialCustomerData changes to null
        return <div className="bg-white shadow-md rounded-lg p-4 mt-4 text-gray-500">Loading customer information...</div>;
    }

    // The modal structure provided previously assumed it was rendered within another modal container (like in Header.tsx).
    // If this component *is* the modal itself, it needs the outer structure.
    // If it's rendered *inside* another modal container, the outer structure here is redundant.
    // Assuming this component is rendered *inside* the container managed by the parent (Header or ProductCard).

    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-50 p-4"> {/* Overlay */}
            <div className="relative bg-gray-100 rounded-lg shadow-xl w-[80vw] max-w-4xl max-h-[80vh] overflow-y-auto p-6"> {/* Container */}
                {/* Close button */}
                {/* Close button - only render if onClose prop is provided */}
            {onClose && (
                <button
                    onClick={onClose}
                    className="absolute top-2 right-3 text-gray-600 hover:text-gray-900 text-2xl font-bold z-10"
                    aria-label="Close profile modal"
                >
                    <FiX size={24} />
                </button>
            )}

            {/* Main Content */}
            <div className="bg-white shadow-md rounded-lg p-4 mt-4">
                {/* Header */}
                <div className={`flex justify-between items-center cursor-pointer ${isCollapsed ? '' : ' bg-green-200 rounded-lg p-2'}`}
                    onClick={toggleCollapse}>
                    <h3 className="text-sm font-semibold">Customer Information</h3>
                    <button className="text-blue-600 hover:text-blue-800 focus:outline-none" aria-expanded={!isCollapsed}>
                        {isCollapsed ? <FiChevronDown size={20} /> : <FiChevronUp size={20} />}
                    </button>
                </div>

                {/* Collapsible Content */}
                <div className={`mt-2 ${isCollapsed ? 'hidden' : 'block'}`}>
                    <EditableField label="Name" value={customer.name} field="name" onUpdate={handleApiUpdate} />
                    <EditableField label="First Name" value={customer.first_name} field="first_name" onUpdate={handleApiUpdate} required />
                    <EditableField label="Last Name" value={customer.last_name} field="last_name" onUpdate={handleApiUpdate} required />

                    <hr className="my-2" />
                    <h4 className="text-xs font-semibold mb-1 text-gray-600">Address & Contact</h4>

                    {/* Location Dropdowns */}
                    <div className="flex items-center my-1 min-h-[28px]">
                        <strong className="w-28 inline-block flex-shrink-0 text-sm">Country:<span className="text-red-500 ml-1">*</span></strong>
                        <select value={selectedCountry?.name || ''} onChange={handleCountryChange} className={`border px-1 py-0.5 text-sm w-full rounded flex-grow mr-2 bg-white disabled:bg-gray-200 ${!selectedCountry?.name ? 'border-red-500' : 'border-gray-300'}`} disabled={isUpdating || loadingCountries}>
                            <option value="">{loadingCountries ? 'Loading...' : '-- Select Country --'}</option>
                            {countryOptions.map(country => (<option key={country.id ?? country.code} value={country.name}>{country.name}</option>))}
                        </select>
                        {/* Removed placeholder div */}
                    </div>
                    <div className="flex items-center my-1 min-h-[28px]">
                        <strong className="w-28 inline-block flex-shrink-0 text-sm">State:<span className="text-red-500 ml-1">*</span></strong>
                        <select value={selectedState?.name || ''} onChange={handleStateChange} className={`border px-1 py-0.5 text-sm w-full rounded flex-grow mr-2 bg-white disabled:bg-gray-200 ${!selectedState?.name && selectedCountry ? 'border-red-500' : 'border-gray-300'}`} disabled={!selectedCountry || isUpdating || loadingStates}>
                            <option value="">{loadingStates ? 'Loading...' : '-- Select State --'}</option>
                            {!loadingStates && stateOptions.map(state => (<option key={state.id ?? state.name} value={state.name}>{state.name}</option>))}
                        </select>
                        {/* Removed placeholder div */}
                    </div>
                    <div className="flex items-center my-1 min-h-[28px]">
                        <strong className="w-28 inline-block flex-shrink-0 text-sm">City:<span className="text-red-500 ml-1">*</span></strong>
                        <select value={customer.city || ''} onChange={handleCityChange} className={`border px-1 py-0.5 text-sm w-full rounded flex-grow mr-2 bg-white disabled:bg-gray-200 ${!customer.city && selectedState ? 'border-red-500' : 'border-gray-300'}`} disabled={!selectedState || isUpdating || loadingCities}>
                            <option value="">{loadingCities ? 'Loading...' : '-- Select City --'}</option>
                            {!loadingCities && cityOptions.map(city => (<option key={city.id ?? city.name} value={city.name}>{city.name}</option>))}
                        </select>
                        {/* Removed placeholder div */}
                    </div>

                    <EditableField
                        label="Phone"
                        value={customer.phone}
                        field="phone"
                        onUpdate={handleApiUpdate}
                        required
                        inputType="tel"
                        reminder={phoneReminder}
                    />
                    <EditableField label="Email" value={customer.email} field="email" onUpdate={handleApiUpdate} required inputType="email" />
                </div>
            </div>

            {/* Optional: Add a dedicated "Done" or "Close" button at the bottom if onClose is provided */}
            {onClose && (
                <div className="mt-6 flex justify-end">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                    >
                        Close
                    </button>
                </div>
            )}
            
            </div>
        </div>
    );
};

export default CustomerProfileModal;
